/**
 * Phase 20E: discipline visit notes defined as data (constants/noteTemplates.ts, generated from the
 * backend). One form state, payload builder and validator serve every note template; the local
 * entry type is the API type in lower case (psychosocial_note, rt_treatment, ...).
 */
import { NOTE_TEMPLATES } from '../../../../constants/noteTemplates';
import type { NoteTemplate, NoteTemplateField } from '../../../../constants/noteTemplates';
import { disciplineForProviderType, getDisciplineEntry } from '../../../../constants/disciplines';

export type TemplateNoteValue = string | string[] | boolean;
export type TemplateNoteFormData = Record<string, TemplateNoteValue>;

export const emptyTemplateNote = (): TemplateNoteFormData => ({});

/** The note template of a local or API entry type, or null */
export const noteTemplateFor = (type: string | null | undefined): NoteTemplate | null =>
  NOTE_TEMPLATES.find((t) => t.entryType.toLowerCase() === String(type || '').toLowerCase()) || null;

export const isNoteTemplateType = (type: string | null | undefined): boolean => Boolean(noteTemplateFor(type));

/** Local entry type of a discipline's visit note (from the registry), or null */
export const noteLocalTypeForDiscipline = (discipline: string | null | undefined): string | null => {
  const noteType = getDisciplineEntry(disciplineForProviderType(discipline))?.noteType;
  return noteType ? noteType.toLowerCase() : null;
};

const filled = (v: TemplateNoteValue | undefined): boolean =>
  typeof v === 'boolean' ? true : Array.isArray(v) ? v.length > 0 : typeof v === 'string' && v.trim() !== '';

/** Request data: filled fields only; numbers as numbers */
export function buildTemplateNote(template: NoteTemplate, form: TemplateNoteFormData): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of template.fields) {
    const v = form[f.key];
    if (!filled(v)) continue;
    if (f.type === 'number') out[f.key] = Number(String(v).trim());
    else if (f.type === 'text' || f.type === 'textarea' || f.type === 'select') out[f.key] = String(v).trim();
    else out[f.key] = v;
  }
  return out;
}

/** Entry data -> form */
export function templateNoteForm(template: NoteTemplate, data: unknown): TemplateNoteFormData {
  const d = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const out: TemplateNoteFormData = {};
  for (const f of template.fields) {
    const v = d[f.key];
    if (v === undefined || v === null) continue;
    if (f.type === 'multiselect') out[f.key] = Array.isArray(v) ? v.map(String) : [];
    else if (f.type === 'boolean') out[f.key] = v === true;
    else out[f.key] = String(v);
  }
  return out;
}

const fieldError = (f: NoteTemplateField, v: TemplateNoteValue | undefined): string | null => {
  if (!filled(v)) return f.required ? 'Required' : null;
  if (f.type === 'number') {
    const n = Number(String(v).trim());
    if (!Number.isFinite(n) || (f.min !== undefined && n < f.min) || (f.max !== undefined && n > f.max)) {
      return f.min !== undefined ? `Range: ${f.min}–${f.max}` : 'Enter a number';
    }
  }
  return null;
};

/** Client checks that mirror noteTemplateRules on the server (required, number ranges) */
export function validateTemplateNote(template: NoteTemplate, form: TemplateNoteFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of template.fields) {
    const e = fieldError(f, form[f.key]);
    if (e) errors[f.key] = e;
  }
  return errors;
}
